"use client";
import Title from "@/components/ui/title";
import { DirectusImage } from "@/components/DirectusImage";
import RouterLink from "@/components/ui/router-link";
import { ChevronRight } from "lucide-react";
import { useScopedI18n } from "@/messages/client";
import { useCallback, useEffect, useState } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import { cn } from "@/lib/utils";

export default function CaseOverviewCilent(props: {
  data?: {
    slug?: string;
    title?: string;
    background?: string;
    background_image?: string;
  }[];
}) {
  const t = useScopedI18n("应用");
  const [isMobile, setIsMobile] = useState<boolean>();
  const [current, setCurrent] = useState(0);
  const [mainApi, setMainApi] = useState<CarouselApi | null>(null);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onSelect = useCallback(() => {
    if (!mainApi) {
      return;
    }
    setCurrent(mainApi.selectedScrollSnap() || 0);
  }, [mainApi]);

  useEffect(() => {
    if (!mainApi) {
      return;
    }
    setScrollSnaps(mainApi.scrollSnapList());

    mainApi.on("reInit", () => {
      setScrollSnaps(mainApi.scrollSnapList());
    });
  }, [mainApi]);

  useEffect(() => {
    if (!mainApi) {
      return;
    }
    onSelect();
    mainApi.on("select", onSelect).on("reInit", onSelect);
  }, [mainApi, onSelect]);

  useEffect(() => {
    const listener = () => {
      setIsMobile(
        window.matchMedia("(max-width: 768px)").matches
      );
    }
    listener();
    window.addEventListener("resize", listener);
    return () => {
      window.removeEventListener("resize", listener);
    }
  }, []);

  return (
    <div className="w-full">
      <Title title={t("客户案例")} underline={false} />
      {!isMobile && (
        <div className="flex mt-14 gap-x-7">
          {props.data?.map((el, index) => (
            <RouterLink
              key={index}
              className="w-1/3 h-[343px] bg-white rounded-lg group hover-outline-shadow"
              href={el.slug ? `/case/${el.slug}` : undefined}
            >
              <div className="flex items-center justify-center h-[214px] rounded-t-lg overflow-hidden">
                <DirectusImage
                  id={el.background_image || ""}
                  alt={el.title || ""}
                  zoomInGroup
                  width={380}
                  height={214}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="px-7 pt-5">
                <p className="text-lg font-bold line-clamp-1 whitespace-pre-line text-ellipsis">{el.title}</p>
                <p className="text-ellipsis line-clamp-2 text-gray text-sm leading-6 mt-2 before:float-right before:h-[1.5rem]">
                  <span className="flex items-center justify-end float-right clear-both w-[60%] h-[1.5rem] leading-6 text-xs text-zinc-400">
                    {t("阅读客户案例")}
                    <ChevronRight className="ml-0.5" size={14} />
                  </span>
                  {el.background}
                </p>
              </div>
            </RouterLink>
          ))}
        </div>
      )}
      {isMobile && (
        <Carousel
          className="mt-5 w-full"
          opts={{ loop: true }}
          setApi={setMainApi}
        >
          <CarouselContent className="-ml-2">
            {props.data?.map((el, index) => (
              <CarouselItem
                key={index}
                className="pl-2"
              >
                <RouterLink
                  className="w-full h-[327px]"
                  href={el.slug ? `/case/${el.slug}` : undefined}
                >
                  <div className="flex items-center justify-center w-full h-[183px] overflow-hidden rounded-t-lg">
                    <DirectusImage
                      id={el.background_image || ""}
                      alt={el.title || ""}
                      width={325}
                      height={183}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex flex-col px-4 pt-4 pb-5 w-full h-[144px] bg-white rounded-b-lg">
                    <p className="whitespace-pre-line text-base line-clamp-1 text-ellipsis">{el.title}</p>
                    <p className="mt-1 text-sm h-12 line-clamp-2 text-ellipsis leading-6 text-gray">{el.background}</p>
                    <span className="self-end mt-1 text-xs text-gray">
                      {t("阅读客户案例")}
                      <ChevronRight className="inline" size={12} />
                    </span>
                  </div>
                </RouterLink>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="mt-4 flex w-full items-center justify-center space-x-2 md:hidden">
            {scrollSnaps.map((_, index) => (
              <div
                key={index}
                className={cn(
                  "h-1.5 w-1.5 rounded-full bg-[#CCCCCC] transition-all duration-300 ease-in-out",
                  {
                    "w-4 bg-[#2C2C2C]": current === index,
                  }
                )}></div>
            ))}
          </div>
        </Carousel>
      )}
    </div>
  )
}